import { Button } from "matsuri-ui"
import dayjs from "dayjs"
import { css } from "@emotion/react"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import {
    Card,
    CardActions,
    CategoryBadge,
    DetailLabel,
    DetailList,
    DetailRow,
    DetailValue,
    ReportedAt,
    SummaryThumbnail,
    SummaryThumbnailGrid,
    SummaryThumbnailWrapper
} from "../styled"
import { LostItemReport } from "@/hooks/useLostItemReport"
import { useTranslation } from "@/hooks/useTranslation"

interface Props {
    report: LostItemReport
    deleting: boolean
    onOpenPhoto: (photo: { id: string; url: string }) => void
    onDelete: (report: LostItemReport) => void
}

/**
 * 入口の画面に並べる報告済みの1件。
 *
 * 一覧なので備考は省き、「何を・どこに置いたか」が分かる分だけに絞る
 * （1件でフッターや見出しが画面外へ押し出されると、続けて報告する導線を見失うため）。
 * 写真は最大3枚なので全部並べ、どれでもタップで拡大できるようにする。
 */
export const ReportSummaryCard = ({
    report,
    deleting,
    onOpenPhoto,
    onDelete
}: Props) => {
    const { t } = useTranslation()

    return (
        <Card elevation={1}>
            <CategoryBadge valuable={report.category === "valuable"}>
                {report.category === "valuable" ? t("貴重品") : t("一般品")}
            </CategoryBadge>

            <DetailList>
                <DetailRow>
                    <DetailLabel>{t("物品の説明")}</DetailLabel>
                    <DetailValue>{report.itemDescription}</DetailValue>
                </DetailRow>
                <DetailRow>
                    <DetailLabel>{t("保管場所")}</DetailLabel>
                    <DetailValue>{report.storagePlace}</DetailValue>
                </DetailRow>
            </DetailList>

            {report.images.length > 0 && (
                <SummaryThumbnailGrid>
                    {report.images.map(image => (
                        <SummaryThumbnailWrapper
                            key={image.id}
                            role="button"
                            tabIndex={0}
                            aria-label={t("写真を拡大表示")}
                            onClick={() =>
                                onOpenPhoto({
                                    id: image.id,
                                    url: image.imageUrl
                                })
                            }
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault()
                                    onOpenPhoto({
                                        id: image.id,
                                        url: image.imageUrl
                                    })
                                }
                            }}
                        >
                            <SummaryThumbnail src={image.imageUrl} alt="" />
                        </SummaryThumbnailWrapper>
                    ))}
                </SummaryThumbnailGrid>
            )}

            <CardActions>
                <ReportedAt>
                    <ScheduleRoundedIcon style={{ fontSize: 14 }} />
                    {dayjs.unix(report.reportedAt).format("M/D HH:mm")}
                </ReportedAt>

                {/* 通知済みの報告は WORK が回収に動いている可能性があるため取り消させない */}
                {report.isDeletable && (
                    <Button
                        variant="text"
                        color="error"
                        size="small"
                        disabled={deleting}
                        onClick={() => onDelete(report)}
                        css={css`
                            margin-left: auto;
                        `}
                    >
                        {t("取り消す")}
                    </Button>
                )}
            </CardActions>
        </Card>
    )
}
